import {
    AdminPreferences,
    IMetrics,
    InstanceConfigConstants,
    KendraConst,
    SearchInterface,
    Stage
} from "@amzn/ask-legal-domain";
import { Alert, Pagination, Spinner, Tabs, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Builder } from "builder-pattern";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { useCrossInstanceSearch } from "../../hooks/use-cross-instance-search";
import { useInstanceById } from "../../contexts/InstanceDataContext";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { SearchResultPanel } from "../../components/search/ResultPanel";
import { LoadingSpinner } from "../../components/common/loading-spinner";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { SourceMatterSmartSearch } from "../../components/mfe/SourceMatterSmartSearch";
import { SmartSearchFilterView } from "../../components/search/search-components/SmartSearchFilterView";
import { SearchStreamingContainer } from "../../components/search/search-components/SearchStreamingContainer";
import { UIModel } from "../../model/ui-model";
import "../../styles/component/layout/SearchResultPageLayout.scss";
import "../../styles/component/layout/PageBaseLayout.scss";
import "../../styles/component/search/automated-answer.scss";
import { InlineSearchChat } from "../../components/search/InlineSearchChat";
import { Preference } from "../../setup/preference";

interface SearchHomeProps {
    instanceId: string;
    queryText?: string;
}

const INITIAL_PAGE_SIZE = 10;
const INITIAL_PAGE_NUMBER = 1;

export const SearchHome: React.FC<SearchHomeProps> = ({ instanceId, queryText }) => {
    if (!queryText?.trim()) {
        return (
            <div className="top-margin-5p">
                <Alert
                    type="error"
                    header="Cannot search for an empty query"
                >
                    <TextContent>
                        Please provide a valid search query and try again.
                    </TextContent>
                </Alert>
            </div>
        );
    }

    const DEFAULT_AI_SEARCH_SUMMARY_ENABLED = AdminPreferences.DEFAULT_ADMIN_PREFERENCES.search.aiSearchSummaryEnabled;
    const context = React.useContext(AppContext);
    const { instance, isLoading: isLoadingInstance, hasError: hasInstanceError } = useInstanceById(instanceId);
    const searchRunner = useAPI(
        context.getSearchAPI().search
    );
    const recordViewHistoryRunner = useAPI(
        context.getCommonAPI().recordViewHistory
    );
    const navigate = useNavigate();
    const isCrossInstanceSearchEnabled = useCrossInstanceSearch();
    const queryInputState = UIModel.State.use<SearchInterface.SearchQueryInput>({});

    const [authors, setAuthors] = React.useState<string[]>([]);
    const [pageLibs, setPageLibs] = React.useState<string[]>([]);
    const [availableInstances, setAvailableInstances] = React.useState<string[]>([]);
    const [instancePreferences, setInstancePreferences] = React.useState<AdminPreferences.InstancePreferences>();
    const [hasLegalOnlyAccess, setHasLegalOnlyAccess] = React.useState(false);

    const initAuthorOptions = React.useCallback(() => {
        const allAuthors: string[] = [];
        searchRunner.data.output.result.ResultItems.forEach(r => {
            const author = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Default.Authors
            );
            if (author?.Value?.StringListValue) {
                allAuthors.push(...author.Value.StringListValue);
            }
        });
        setAuthors([...new Set(allAuthors)]);
    }, [searchRunner.data.output]);

    const initPageLibraries = React.useCallback(() => {
        const pageLibs: string[] = [];
        searchRunner.data.output.result.ResultItems.forEach(r => {
            const pageLib = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.PageLibraryId
            );
            if (pageLib?.Value?.StringValue) {
                pageLibs.push(pageLib.Value.StringValue);
            }
        });
        setPageLibs([...new Set(pageLibs)]);
    }, [searchRunner.data.output]);

    const initAvailableInstances = React.useCallback(() => {
        if (!searchRunner.data.output?.result?.FacetResults) return;
        const instanceFacet = searchRunner.data.output.result.FacetResults.find(facet =>
            facet.DocumentAttributeKey === KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
        );
        const instancesFromSearch = instanceFacet?.DocumentAttributeValueCountPairs
            ?.filter(pair => pair.DocumentAttributeValue?.StringValue)
            .map(pair => pair.DocumentAttributeValue.StringValue as string) || [];
        const allInstances = [...instancesFromSearch];
        if (!allInstances.includes(instanceId)) {
            allInstances.push(instanceId);
        }
        setAvailableInstances(allInstances);
    }, [searchRunner.data.output, instanceId]);

    const getClientSidePaginatedSearchResults = React.useCallback(() => {
        const { pageNumber, pageSize } = queryInputState.value;
        const startIndex = (pageNumber - 1) * pageSize;
        const endIndex = pageNumber * pageSize;
        const paginatedResults = searchRunner.data.output.result.ResultItems.slice(startIndex, endIndex);

        return SearchInterface.SearchQueryResponse.create({
            result: {
                ...searchRunner.data.output.result,
                ResultItems: paginatedResults
            },
            isLegalOnlyAuthorized: searchRunner.data.output.isLegalOnlyAuthorized
        });
    }, [queryInputState.value, searchRunner.data.output]);

    const streamingRequest = React.useMemo(() => {
        if (!queryInputState.value) return null;

        return {
            query: queryInputState.value.queryText,
            instanceId: instanceId,
            showStrictlyLegalOnly: queryInputState.value.showStrictlyLegalOnly ?? false,
            showSharedContent: queryInputState.value.showSharedContent ?? false,
            filterConfiguration: queryInputState.value.filterConfiguration
        };
    }, [
        queryInputState.value?.queryText,
        instanceId,
        queryInputState.value?.showStrictlyLegalOnly,
        queryInputState.value?.showSharedContent,
        queryInputState.value?.filterConfiguration
    ]);

    const constructSearchQueryInput = React.useCallback((
        queryText: string,
        preferences?: AdminPreferences.InstancePreferences
    ): SearchInterface.SearchQueryInput => {
        const instanceConfig = InstanceConfigConstants.getInstanceConfig(
            context.getStage() as Stage,
            instanceId
        );
        const languageCode = instanceConfig
            ? InstanceConfigConstants.getDefaultSearchLanguageCode(context.getStage() as Stage, instanceId)
            : undefined;
        let searchQuery = SearchInterface.SearchQueryInput.create({
            instanceId: instanceId,
            queryText: queryText,
        }, {
            languageCode: languageCode,
            pageSize: INITIAL_PAGE_SIZE,
            pageNumber: INITIAL_PAGE_NUMBER,
            isCrossInstanceSearchLabEnabled: isCrossInstanceSearchEnabled
        });
        if (preferences) {
            if (preferences.search.filterPrefs.showFileType.length > 0) {
                preferences.search.filterPrefs.showFileType.forEach(t => {
                    searchQuery = SearchInterface.SearchQueryInput.applyFilter(searchQuery, {
                        attribute: KendraConst.ContainerIngestionKendraAttributes.Custom.ContentType,
                        operation: "EqualsTo",
                        valueMap: {
                            value: t,
                            valueType: KendraConst.IndexAttributeType.STRING
                        }
                    });
                });
            }
        }
        return searchQuery;
    }, [context, instanceId, isCrossInstanceSearchEnabled]);

    React.useEffect(() => {
        if (!instance) return;
        if (instance.preferences) {
            setInstancePreferences(instance.preferences);
        } else {
            setInstancePreferences(AdminPreferences.DEFAULT_ADMIN_PREFERENCES);
        }
    }, [instance]);

    const [isQueryInitialized, setIsQueryInitialized] = React.useState(false);
    const lastSearchQueryRef = React.useRef<string>("");

    const createQuerySignature = React.useCallback((query: SearchInterface.SearchQueryInput): string => {
        return JSON.stringify({
            text: query.queryText,
            filters: query.filterConfiguration || [],
            page: query.pageNumber,
            sort: query.sortingConfiguration || []
        });
    }, []);

    const executeSearchImmediate = React.useCallback((query: SearchInterface.SearchQueryInput) => {
        const querySignature = createQuerySignature(query);
        if (querySignature === lastSearchQueryRef.current) {
            return;
        }
        lastSearchQueryRef.current = querySignature;
        searchRunner.submitRun(query);
        recordViewHistoryRunner.submitRun(
            IMetrics.RecordViewHistoryInput.create({
                url: `/search/${instanceId}?queryText=${queryText}`,
                entityId: instanceId,
                type: "Search"
            })
        );
    }, [createQuerySignature, searchRunner, recordViewHistoryRunner, instanceId, queryText]);

    React.useEffect(() => {
        if (!instancePreferences) return;

        queryInputState.setValue(constructSearchQueryInput(queryText, instancePreferences));
        setAuthors([]);
        setPageLibs([]);
        setIsQueryInitialized(true);
    }, [queryText, instancePreferences, constructSearchQueryInput]);

    React.useEffect(() => {
        if (!isQueryInitialized || !queryInputState.value?.queryText?.length || !instancePreferences) {
            return;
        }

        executeSearchImmediate(queryInputState.value);
    }, [queryInputState.value, instancePreferences, isQueryInitialized, executeSearchImmediate]);

    React.useEffect(() => {
        if (searchRunner.status !== "Succeeded" || !searchRunner.data.output) return;

        if (authors.length === 0) {
            initAuthorOptions();
        }
        if (pageLibs.length === 0) {
            initPageLibraries();
        }
        if (availableInstances.length === 0 && isCrossInstanceSearchEnabled) {
            initAvailableInstances();
        }
        setHasLegalOnlyAccess(searchRunner.data.output.isLegalOnlyAuthorized);
    }, [
        searchRunner.status,
        searchRunner.data.output,
        authors.length,
        pageLibs.length,
        availableInstances.length,
        isCrossInstanceSearchEnabled,
        initAuthorOptions,
        initPageLibraries,
        initAvailableInstances
    ]);

    return (
        <React.Fragment>
            {hasInstanceError &&
                <div className="top-margin-5p">
                    <PermissionDenied
                        entityId={instanceId}
                        accessType="HomePage"
                    />
                </div>
            }
            {isLoadingInstance &&
                <div className="vertical-center top-margin-5p">
                    <Spinner />&nbsp;Loading search module...
                </div>
            }
            {instance && queryInputState.value &&
                <div className="search-page-layout">
                    {!instancePreferences?.search?.sourceInstances?.length && (
                        <>
                            <div className="search-page-layout-filter">
                                <SmartSearchFilterView
                                    preferences={instancePreferences.search}
                                    queryInputState={queryInputState}
                                    authors={authors}
                                    pageLibraries={pageLibs}
                                    availableInstances={availableInstances}
                                    hasLegalOnlyAccess={hasLegalOnlyAccess}
                                    isSearchRunning={searchRunner.status === "Running"}
                                />
                            </div>
                            <div className="search-page-layout-result">
                                {/* Inline Chat Agent - rendered on top of search results */}
                                {context.hasLab(Preference.Lab.QuickChatAgent) && context.config?.quickAgentChat?.url && queryInputState.value?.queryText && (
                                    <InlineSearchChat
                                        agentUrl={context.config.quickAgentChat.url}
                                        query={queryInputState.value.queryText}
                                    />
                                )}
                                {/* Streaming Summary Content - Show when query exists */}
                                {(instancePreferences?.search?.aiSearchSummaryEnabled ?? DEFAULT_AI_SEARCH_SUMMARY_ENABLED) &&
                                 streamingRequest && (
                                    <SearchStreamingContainer
                                        request={streamingRequest}
                                        shouldStart={!!queryInputState.value?.queryText}
                                    />
                                )}
                                {searchRunner.status === "Running" &&
                                    <div className="vertical-center top-margin-5p">
                                        <LoadingSpinner msg={`Searching for ${queryInputState.value.queryText} ...`}/>
                                    </div>
                                }
                                {searchRunner.status === "Error" && (
                                    searchRunner.data.err.code === 403 ?
                                        <PermissionDenied entityId={instanceId} accessType="HomePage"/> :
                                        <ErrorFlashbar error={searchRunner.data.err}/>
                                )}
                                {searchRunner.status === "Succeeded" && !!searchRunner.data.output && (
                                    <React.Fragment>
                                        <h4>
                                            Results for <em>"{queryInputState.value.queryText}"</em>
                                        </h4>
                                        {!!searchRunner.data.output.result.SpellCorrectedQueries &&
                                            <div className="search-result-spell-correction">
                                                {searchRunner.data.output.result.SpellCorrectedQueries.map(spellCorrectionQuery => (
                                                    <div>Do you mean "<a onClick={() => {
                                                        queryInputState.setValue(
                                                            constructSearchQueryInput(spellCorrectionQuery.SuggestedQueryText, instancePreferences)
                                                        );
                                                        // Update URL without triggering page reload
                                                        navigate(`/search/${instanceId}?queryText=${spellCorrectionQuery.SuggestedQueryText}`, { replace: true });
                                                    }}>{spellCorrectionQuery.SuggestedQueryText}</a>"?</div>
                                                ))}
                                                <br />
                                            </div>
                                        }
                                        <SearchResultPanel
                                            results={getClientSidePaginatedSearchResults()}
                                            instanceId={instanceId}
                                            queryInputState={queryInputState}
                                        />
                                        <nav className="centered-pagination" aria-label="pagination">
                                            <Pagination
                                                ariaLabels={{
                                                    nextPageLabel: "Next page",
                                                    previousPageLabel: "Previous page",
                                                    pageLabel: pageNumber =>
                                                        `Page ${pageNumber} of all pages`
                                                }}
                                                currentPageIndex={queryInputState.value.pageNumber}
                                                onChange={({ detail }) =>
                                                    queryInputState.setValue(
                                                        Builder<SearchInterface.SearchQueryInput>(queryInputState.value)
                                                            .pageNumber(detail.currentPageIndex)
                                                            .build()
                                                    )
                                                }
                                                pagesCount={Math.ceil(
                                                    searchRunner.data.output.result.TotalNumberOfResults / queryInputState.value.pageSize
                                                )}
                                            />
                                        </nav>
                                    </React.Fragment>
                                )}
                            </div>
                        </>
                    )}
                    {!!instancePreferences.search.sourceInstances?.length && (
                        <Tabs tabs={[
                            { label: "Pathfinder Results", id: "Pathfinder", content:
                                <div className="search-page-layout">
                                    <div className="search-page-layout-filter">
                                        <SmartSearchFilterView
                                            preferences={instancePreferences.search}
                                            queryInputState={queryInputState}
                                            authors={authors}
                                            pageLibraries={pageLibs}
                                            availableInstances={availableInstances}
                                            hasLegalOnlyAccess={hasLegalOnlyAccess}
                                            isSearchRunning={searchRunner.status === "Running"}
                                        />
                                    </div>
                                    <div className="search-page-layout-result">
                                        {/* Inline Chat Agent - rendered on top of search results */}
                                        {context.hasLab(Preference.Lab.QuickChatAgent) && context.config?.quickAgentChat?.url && queryInputState.value?.queryText && (
                                            <InlineSearchChat
                                                agentUrl={context.config.quickAgentChat.url}
                                                query={queryInputState.value.queryText}
                                            />
                                        )}
                                        {/* Streaming Summary Content - Show when query exists */}
                                        {(instancePreferences?.search?.aiSearchSummaryEnabled ?? DEFAULT_AI_SEARCH_SUMMARY_ENABLED) &&
                                         streamingRequest && (
                                            <SearchStreamingContainer
                                                request={streamingRequest}
                                                shouldStart={!!queryInputState.value?.queryText}
                                            />
                                        )}
                                        {searchRunner.status === "Running" &&
                                            <div className="vertical-center top-margin-5p">
                                                <LoadingSpinner msg={`Searching for ${queryInputState.value.queryText} ...`}/>
                                            </div>
                                        }
                                        {searchRunner.status === "Error" && (
                                            searchRunner.data.err.code === 403 ?
                                                <PermissionDenied entityId={instanceId} accessType="HomePage"/> :
                                                <ErrorFlashbar error={searchRunner.data.err}/>
                                        )}
                                        {searchRunner.status === "Succeeded" && !!searchRunner.data.output && (
                                            <React.Fragment>
                                                <h4>
                                                    Results for <em>"{queryInputState.value.queryText}"</em>
                                                </h4>
                                                {!!searchRunner.data.output.result.SpellCorrectedQueries &&
                                                    <div className="search-result-spell-correction">
                                                        {searchRunner.data.output.result.SpellCorrectedQueries.map(spellCorrectionQuery => (
                                                            <div>Do you mean "<a onClick={() => {
                                                                queryInputState.setValue(
                                                                    constructSearchQueryInput(spellCorrectionQuery.SuggestedQueryText, instancePreferences)
                                                                );
                                                                // Update URL without triggering page reload
                                                                navigate(`/search/${instanceId}?queryText=${spellCorrectionQuery.SuggestedQueryText}`, { replace: true });
                                                            }}>{spellCorrectionQuery.SuggestedQueryText}</a>"?</div>
                                                        ))}
                                                        <br />
                                                    </div>
                                                }
                                                <SearchResultPanel
                                                    results={getClientSidePaginatedSearchResults()}
                                                    instanceId={instanceId}
                                                    queryInputState={queryInputState}
                                                />
                                                <nav className="centered-pagination" aria-label="pagination">
                                                    <Pagination
                                                        ariaLabels={{
                                                            nextPageLabel: "Next page",
                                                            previousPageLabel: "Previous page",
                                                            pageLabel: pageNumber =>
                                                                `Page ${pageNumber} of all pages`
                                                        }}
                                                        currentPageIndex={queryInputState.value.pageNumber}
                                                        onChange={({ detail }) =>
                                                            queryInputState.setValue(
                                                                Builder<SearchInterface.SearchQueryInput>(queryInputState.value)
                                                                    .pageNumber(detail.currentPageIndex)
                                                                    .build()
                                                            )
                                                        }
                                                        pagesCount={Math.ceil(
                                                            searchRunner.data.output.result.TotalNumberOfResults / queryInputState.value.pageSize
                                                        )}
                                                    />
                                                </nav>
                                            </React.Fragment>
                                        )}
                                    </div>
                                </div>
                            },
                            instancePreferences.search.sourceInstances?.length && {
                                label: "Source Results",
                                id: "Source",
                                content: <>
                                    <SourceMatterSmartSearch
                                        instanceId={instancePreferences?.search?.sourceInstances?.[0]?.id}
                                        filterText={queryInputState?.value?.queryText}
                                    />
                                </>
                            }]}
                        />
                    )}
                </div>
            }
        </React.Fragment>
    );
};
